import React, {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useRef,
} from 'react';

import { getPageTime } from '../utils/performance';

// Per-card-open timer. Started on hover/click, read by analytics emitters
// for realistic durations. `getDurationSinceInteraction` returns undefined
// until the first `markInteractionStart` so emitters can distinguish
// "no interaction yet" from "duration was zero".
type CardOpenTimerContextValue = {
	markInteractionStart: () => void;
	getDurationSinceInteraction: () => number | undefined;
};

const noopContext: CardOpenTimerContextValue = {
	markInteractionStart: () => {},
	getDurationSinceInteraction: () => undefined,
};

const CardOpenTimerContext = createContext<CardOpenTimerContextValue>(noopContext);

/**
 * Provides a per-card timer. Nests safely: when wrapped inside another
 * `CardOpenTimerProvider` it transparently uses the outer one.
 */
export function CardOpenTimerProvider({ children }: { children: ReactNode }): React.JSX.Element {
	const outer = useContext(CardOpenTimerContext);
	const interactionStartRef = useRef<number | null>(null);

	const markInteractionStart = useCallback(() => {
		// Re-arm per interaction so successive opens each measure from their own t=0.
		interactionStartRef.current = getPageTime();
	}, []);

	const getDurationSinceInteraction = useCallback((): number | undefined => {
		if (interactionStartRef.current === null) {
			return undefined;
		}
		return Math.max(0, getPageTime() - interactionStartRef.current);
	}, []);

	const value = useMemo(
		() => ({ markInteractionStart, getDurationSinceInteraction }),
		[markInteractionStart, getDurationSinceInteraction],
	);

	if (outer !== noopContext) {
		return <>{children}</>;
	}

	return <CardOpenTimerContext.Provider value={value}>{children}</CardOpenTimerContext.Provider>;
}

// Returns the timer helpers. Outside a provider, returns a safe no-op pair so
// leaf-component unit tests do not need to wire the provider just for analytics.
export function useCardOpenTimer(): CardOpenTimerContextValue {
	return useContext(CardOpenTimerContext);
}
